import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../utils/api';
import { useToast } from './Toast';
import { Search, UserCheck, Check, ChevronDown, UserPlus, Loader, X } from 'lucide-react';

// 代提代传共用的“选择对象”卡片（证券 / 排五的代提代传页都用它）。
//   没选时：展开的搜索 + 列表；选好后：自动折叠成一行“已选择：xxx  [更换]”，点“更换”再展开。
//   管理员可选“本人”；代理商只能选自己名下的成员 / 外部接单人（列表本身由服务端按权限返回，代理商不显示自己）。
// value：当前选中的对象 { id, name, role, is_external, self? } 或 null；onChange(对象)。
export default function ProxyTargetPicker({ value, onChange, allowSelf = true, hint }) {
  const { user, isAdmin } = useAuth();
  const { showToast } = useToast();
  const [targets, setTargets] = useState([]);
  const [keyword, setKeyword] = useState('');
  const [open, setOpen] = useState(!value);   // 展开：没选时一定展开；选好后自动折叠（切回来重新挂载时，已有选中值就直接折叠）
  // 新建外部接单人：小表单，昵称/账号/手机号至少填一个；建好（或按账号/手机号复用到已有的）自动选中并折叠
  const [showNew, setShowNew] = useState(false);
  const [newName, setNewName] = useState('');
  const [newUsername, setNewUsername] = useState('');
  const [newPhone, setNewPhone] = useState('');
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    let alive = true;
    Promise.all([
      api.get('/users').catch(() => ({ data: { users: [] } })),
      api.get('/users/external').catch(() => ({ data: { users: [] } })),
    ]).then(([users, ext]) => {
      if (!alive) return;
      const merged = [
        ...(users.data.users || []).map(u => ({ id: u.id, name: u.name || u.username || u.phone || `用户${u.id}`, role: u.role, is_external: 0, status: u.status })),
        ...(ext.data.users || []).map(u => ({ id: u.id, name: u.name || u.phone || `外部${u.id}`, role: u.role, is_external: 1, status: 'active' })),
      ];
      const seen = new Set();
      setTargets(merged.filter(u => {
        if (seen.has(u.id)) return false;
        seen.add(u.id);
        if (u.status === 'disabled') return false;
        if (!isAdmin && u.id === user?.id) return false;   // 代理商不能选自己
        return true;
      }));
    });
    return () => { alive = false; };
  }, [isAdmin, user?.id]);

  const filtered = useMemo(() => {
    const k = keyword.trim().toLowerCase();
    if (!k) return targets;
    return targets.filter(t => String(t.name).toLowerCase().includes(k) || String(t.id).includes(k));
  }, [targets, keyword]);

  const self = isAdmin && allowSelf ? { id: user.id, name: '本人（管理员）', role: 'admin', is_external: 0, self: true } : null;

  const pick = (t) => { onChange(t); setOpen(false); setKeyword(''); };

  const createExternal = async () => {
    if (!newName.trim() && !newUsername.trim() && !newPhone.trim()) {
      showToast('请至少填写昵称、账号或手机号', 'error'); return;
    }
    setCreating(true);
    try {
      const res = await api.post('/users/external', { name: newName.trim(), username: newUsername.trim(), phone: newPhone.trim() });
      const created = { id: res.data.id, name: res.data.name, role: 'user', is_external: 1 };
      // 塞进本地列表（去重），这样折叠再展开也能在列表里看到刚建的人，不用重新拉一次
      setTargets(prev => [created, ...prev.filter(t => t.id !== created.id)]);
      pick(created);
      setShowNew(false);
      setNewName(''); setNewUsername(''); setNewPhone('');
      showToast(res.data.reused ? '已选中这个外部接单人' : '已新建并选中', 'success');
    } catch (err) {
      showToast(err.response?.data?.error || '新建失败', 'error');
    } finally { setCreating(false); }
  };

  const badges = (t) => (
    <>
      {t.is_external === 1 && <span className="badge" style={{ marginLeft: 6, fontSize: 10, background: 'rgba(255,149,0,0.12)', color: 'var(--c-accent)' }}>外部</span>}
      {t.role === 'agent' && <span className="badge badge-info" style={{ marginLeft: 6, fontSize: 10 }}>代理</span>}
    </>
  );

  // 已选好且折叠：一行摘要
  if (value && !open) {
    return (
      <div className="card" style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px' }}>
        <UserCheck size={16} color="var(--c-primary)" style={{ flexShrink: 0 }} />
        <span style={{ fontSize: 12, color: 'var(--c-text-tertiary)', flexShrink: 0 }}>已选择</span>
        <span style={{ flex: 1, minWidth: 0, fontSize: 15, fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {value.name}{badges(value)}
        </span>
        <button className="btn btn-outline btn-sm" onClick={() => setOpen(true)} style={{ flexShrink: 0, display: 'flex', alignItems: 'center', gap: 4 }}>
          更换 <ChevronDown size={13} />
        </button>
      </div>
    );
  }

  const row = (t) => {
    const active = value?.id === t.id && !!value?.self === !!t.self;
    return (
      <div key={t.self ? 'self' : t.id} onClick={() => pick(t)} style={{
        display: 'flex', alignItems: 'center', gap: 8, padding: '10px 12px', borderRadius: 10, cursor: 'pointer', marginBottom: 6,
        background: active ? 'var(--c-primary-light)' : 'var(--c-bg)', border: `1.5px solid ${active ? 'var(--c-primary)' : 'transparent'}`,
      }}>
        <span style={{ flex: 1, fontSize: 14, fontWeight: 600 }}>{t.name}{badges(t)}</span>
        {active && <Check size={16} color="var(--c-primary)" />}
      </div>
    );
  };

  return (
    <div className="card">
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
        <UserCheck size={16} color="var(--c-primary)" />
        <span style={{ fontSize: 14, fontWeight: 700 }}>选择对象</span>
        <span style={{ fontSize: 11, color: 'var(--c-text-tertiary)', flex: 1 }}>{hint || (isAdmin ? '系统用户 / 外部接单人 / 本人' : '名下成员 / 外部接单人')}</span>
        {value && <button onClick={() => setOpen(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 12, color: 'var(--c-primary)' }}>收起</button>}
      </div>
      <div style={{ position: 'relative', marginBottom: 8 }}>
        <Search size={14} color="var(--c-text-tertiary)" style={{ position: 'absolute', left: 12, top: 14 }} />
        <input className="input" style={{ paddingLeft: 34, fontSize: 16 }} placeholder="搜索名字或编号" value={keyword} onChange={e => setKeyword(e.target.value)} />
      </div>

      {!showNew ? (
        <button onClick={() => setShowNew(true)} style={{
          display: 'flex', alignItems: 'center', gap: 6, width: '100%', background: 'var(--c-bg)', border: '1px dashed var(--c-border)',
          borderRadius: 10, padding: '9px 12px', marginBottom: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600, color: 'var(--c-primary)',
        }}>
          <UserPlus size={15} /> 新建外部接单人
        </button>
      ) : (
        <div style={{ background: 'var(--c-bg)', borderRadius: 10, padding: 10, marginBottom: 8 }}>
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
            <span style={{ fontSize: 12, fontWeight: 700, flex: 1 }}>新建外部接单人</span>
            <button onClick={() => { setShowNew(false); setNewName(''); setNewUsername(''); setNewPhone(''); }} disabled={creating}
              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2, color: 'var(--c-text-tertiary)' }}>
              <X size={14} />
            </button>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 8 }}>
            <input className="input" style={{ fontSize: 16, height: 36 }} placeholder="昵称（选填）" value={newName} onChange={e => setNewName(e.target.value)} />
            <input className="input" style={{ fontSize: 16, height: 36 }} placeholder="账号（选填，5位数字，不填自动生成）" value={newUsername} onChange={e => setNewUsername(e.target.value.replace(/\D/g, '').slice(0, 5))} />
            <input className="input" style={{ fontSize: 16, height: 36 }} placeholder="手机号（选填，作联系方式）" value={newPhone} onChange={e => setNewPhone(e.target.value.replace(/\D/g, '').slice(0, 11))} />
          </div>
          <div style={{ fontSize: 11, color: 'var(--c-text-tertiary)', marginBottom: 8, lineHeight: 1.5 }}>三项至少填一个；账号或手机号已存在时会直接选中那个人，不会重复建档。</div>
          <button className="btn btn-primary btn-sm" onClick={createExternal} disabled={creating} style={{ width: '100%', height: 38 }}>
            {creating ? <><Loader size={13} className="spin" /> 新建中…</> : '确认新建并选中'}
          </button>
        </div>
      )}

      <div style={{ maxHeight: 240, overflowY: 'auto' }}>
        {self && row(self)}
        {filtered.map(row)}
        {filtered.length === 0 && <p style={{ textAlign: 'center', fontSize: 13, color: 'var(--c-text-tertiary)', padding: 16 }}>没有可选对象</p>}
      </div>
    </div>
  );
}
